import { useState, useCallback } from "react";
import { approvalTemplates } from "../../../mocks/approvals";
import { useRole } from "../../../contexts/RoleContext";
import { useAuth } from "../../../contexts/AuthContext";

interface Props {
  templateId: string;
  onSubmit: (data: Record<string, string>) => void;
  onCancel: () => void;
  resubmitInfo?: { title: string; aiComment: string };
}

// 직무 레이블
const ROLE_LABEL: Record<string, string> = {
  CEO: "대표이사", Finance: "재무담당", Marketing: "마케팅", Legal: "법무담당", AR: "A&R담당",
};

// 워크플로우
const workflowRules: Record<string, Partial<Record<string, { toRole: string; type: string; buildTitle: (d: Record<string, string>) => string; buildBody: (d: Record<string, string>) => string }>>> = {
  AR: {
    tmpl2: { toRole: "Finance", type: "budget_request", buildTitle: (d) => `[A&R] 예산 승인 요청 — ${d["f1"] ?? "프로젝트"}`, buildBody: (d) => `A&R팀에서 '${d["f1"] ?? "프로젝트"}'에 대한 예산 ${Number(d["f2"] ?? 0).toLocaleString()}만원 승인 요청을 제출했습니다.` },
    tmpl3: { toRole: "Finance", type: "release_budget", buildTitle: (d) => `[A&R] 음원 발매 예산 검토 — ${d["f2"] ?? "앨범"}`, buildBody: (d) => `A&R팀에서 '${d["f2"] ?? "앨범"}' 발매 기안을 제출했습니다.` },
  },
  Legal: {
    tmpl1: { toRole: "CEO", type: "contract_sign", buildTitle: () => "[법무] 계약 검토 완료 — 최종 서명 요청", buildBody: (d) => `법무팀이 '${d["f1"] ?? "아티스트"}' 전속 계약서 검토를 완료했습니다. CEO 최종 서명이 필요합니다.` },
    tmpl4: { toRole: "CEO", type: "legal_action", buildTitle: () => "[법무] 법적 대응 최종 결재 요청", buildBody: (d) => `법무팀이 '${d["f1"] ?? "사안"}' 법적 대응 기안서를 제출했습니다.` },
  },
};

// 검증 로직
function validate(templateId: string, data: Record<string, string>): { passed: boolean; message: string } {
  switch (templateId) {
    case "tmpl1": {
      const rev = parseInt(data["f5"] || "0");
      const art = parseInt(data["f6"] || "0");
      const money = parseInt(data["f4"] || "0");
      if (rev + art !== 100) return { passed: false, message: `수익 배분율 합계가 100%여야 합니다. 현재 합계: ${rev + art}%` };
      if (money < 500) return { passed: false, message: "전속 계약금이 너무 낮습니다. 최소 500만원 이상이어야 합니다." };
      if (rev > 80) return { passed: false, message: "회사 배분율 80% 초과는 공정거래 원칙에 위배되는 독소조항입니다." };
      return { passed: true, message: "계약 조건 검토 완료. 수익 배분율과 계약금이 업계 표준 범위 내입니다. 전속 기간도 적정 수준입니다." };
    }
    case "tmpl2": {
      const budget = parseInt(data["f2"] || "0");
      if (budget > 10000) return { passed: false, message: "예산이 현재 가용 한도(1억원)를 초과합니다. 예산 재조정 후 재제출 바랍니다." };
      if (!data["f5"] || data["f5"].length < 20) return { passed: false, message: "'기대 효과' 항목이 충분히 작성되지 않았습니다. 구체적인 ROI 예측을 포함해 주세요." };
      return { passed: true, message: "예산 항목 적정성 검토 완료. 항목별 집행 계획이 적절합니다. 예산 집행 승인 처리됩니다." };
    }
    case "tmpl3": {
      if (!data["f7"] || data["f7"].length < 30) return { passed: false, message: "앨범 콘셉트 요약이 부족합니다. 타깃, 장르, 핵심 메시지를 포함해 주세요." };
      return { passed: true, message: "발매 기안서 검토 완료. 마케팅 예산 및 유통 계획이 적절합니다." };
    }
    default:
      return { passed: true, message: "검토 완료. 기안 내용이 적절합니다. 승인 처리됩니다." };
  }
}

// 문서 번호 생성
function genDocNo(templateId: string): string {
  const prefix: Record<string, string> = { tmpl1: "CONT", tmpl2: "BUDG", tmpl3: "RELS", tmpl4: "LEGL", tmpl5: "ALBM", tmpl6: "LIVE", tmpl7: "GRNT", tmpl8: "IPLC", tmpl9: "CAMP" };
  return `${prefix[templateId] ?? "DOCS"}-2026-${String(Math.floor(100 + Math.random() * 900)).padStart(3, "0")}`;
}

// 입력 공통 스타일
const inputCls = "w-full bg-transparent border-b border-[#BBBBBB] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm py-1 placeholder-[#CCCCCC] transition-colors";
const textareaCls = "w-full bg-[#FAFAFA] border border-[#DDDDDD] focus:border-[#999999] outline-none text-[#1A1A1A] text-sm px-3 py-2 resize-none rounded placeholder-[#BBBBBB] transition-colors leading-relaxed";
const selectCls = "w-full bg-transparent border-b border-[#BBBBBB] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm py-1 cursor-pointer transition-colors";

// ─── 결재란 컴포넌트 ──────────────────────────────────────
function ApprovalGrid({ submitter }: { submitter: string }) {
  return (
    <div className="shrink-0">
      <table className="border-collapse text-[10px] text-[#1A1A1A]" style={{ borderLeft: "1.5px solid #999" }}>
        <tbody>
          <tr>
            {["기안", "검토", "결재"].map(label => (
              <td key={label} className="text-center font-semibold bg-[#F2F2F2] px-4 py-1" style={{ border: "1px solid #999", minWidth: 60 }}>{label}</td>
            ))}
          </tr>
          <tr>
            <td className="text-center px-3 py-3 align-top" style={{ border: "1px solid #DDD", height: 56 }}>
              <div className="text-[9px] text-[#888] mb-1">{submitter}</div>
              <div className="text-[10px] text-[#333] font-medium">(서명)</div>
            </td>
            <td className="text-center px-3 py-3 align-top" style={{ border: "1px solid #DDD" }}>
              <div className="text-[9px] text-[#888] mb-1">재무팀</div>
            </td>
            <td className="text-center px-3 py-3 align-top" style={{ border: "1px solid #DDD" }}>
              <div className="text-[9px] text-[#888] mb-1">대표이사</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── 문서 헤더 ──────────────────────────────────────────
function DocHeader({ docNo, date, submitter, dept }: { docNo: string; date: string; submitter: string; dept: string }) {
  return (
    <div className="flex items-start justify-between mb-1">
      <div>
        <div className="text-[11px] text-[#555] font-bold tracking-widest mb-1">K-ArtPEN ENTERTAINMENT</div>
        <table className="border-collapse text-[10px] text-[#555]">
          <tbody>
            {[["문서번호", docNo], ["기안일자", date], ["기안부서", dept], ["기안자", submitter]].map(([k, v]) => (
              <tr key={k}>
                <td className="pr-3 font-medium text-[#888] py-0.5">{k}</td>
                <td className="text-[#444]">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ApprovalGrid submitter={submitter} />
    </div>
  );
}

// ─── 섹션 라벨 ──────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-3">
      <div className="h-px flex-1 bg-[#E0E0E0]"></div>
      <span className="text-[10px] font-bold text-[#888] uppercase tracking-widest px-2">{children}</span>
      <div className="h-px flex-1 bg-[#E0E0E0]"></div>
    </div>
  );
}

// ─── 필드 래퍼 ──────────────────────────────────────────
function FieldRow({ label, required, children, half }: { label: string; required?: boolean; children: React.ReactNode; half?: boolean }) {
  return (
    <div className={half ? "" : ""}>
      <label className="text-[10px] font-semibold text-[#888] uppercase tracking-wide block mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── 템플릿별 문서 본문 ──────────────────────────────────
function TemplateBody({ templateId, data, onChange }: { templateId: string; data: Record<string, string>; onChange: (id: string, val: string) => void }) {
  const f = (id: string) => data[id] ?? "";
  const ch = (id: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(id, e.target.value);

  switch (templateId) {

    // ── 전속 계약서 ──────────────────────────────────────
    case "tmpl1": return (
      <div>
        <SectionLabel>당사자 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div className="border border-[#E0E0E0] rounded p-4 bg-[#FAFAFA]">
            <div className="text-[10px] font-bold text-[#888] mb-3 flex items-center gap-2"><span className="bg-[#1A1A1A] text-white px-2 py-0.5 rounded text-[9px]">甲</span> 계약자 (회사)</div>
            <div className="space-y-1 text-sm text-[#333]">
              <div>상호: <span className="font-semibold">주식회사 K-ArtPEN 엔터테인먼트</span></div>
              <div>대표: <span className="font-semibold">대표이사</span></div>
              <div>사업자등록번호: <span className="font-semibold">123-45-67890</span></div>
            </div>
          </div>
          <div className="border border-[#E0E0E0] rounded p-4 bg-[#FAFAFA]">
            <div className="text-[10px] font-bold text-[#888] mb-3 flex items-center gap-2"><span className="border border-[#1A1A1A] text-[#1A1A1A] px-2 py-0.5 rounded text-[9px]">乙</span> 계약 아티스트</div>
            <FieldRow label="아티스트명 / 그룹명" required>
              <input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 루나&스타즈" className={inputCls} />
            </FieldRow>
          </div>
        </div>

        <SectionLabel>계약 조건</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="계약 시작일" required><input type="date" value={f("f2")} onChange={ch("f2")} className={inputCls} /></FieldRow>
          <FieldRow label="계약 종료일" required><input type="date" value={f("f3")} onChange={ch("f3")} className={inputCls} /></FieldRow>
          <FieldRow label="전속 계약금 (만원)" required><input type="number" value={f("f4")} onChange={ch("f4")} placeholder="예: 2000" className={inputCls} /></FieldRow>
          <FieldRow label="전담 매니저"><input type="text" value={f("f7")} onChange={ch("f7")} placeholder="담당 매니저 이름" className={inputCls} /></FieldRow>
        </div>

        <SectionLabel>수익 배분 조건</SectionLabel>
        <table className="w-full border-collapse text-sm mb-4">
          <thead><tr className="bg-[#F5F5F5]">{["구분", "배분율 (%)", "비고"].map(h => <th key={h} className="border border-[#DDDDDD] px-4 py-2 text-left text-[11px] font-semibold text-[#666]">{h}</th>)}</tr></thead>
          <tbody>
            <tr>
              <td className="border border-[#DDDDDD] px-4 py-2 text-[#555] text-xs">회사 (甲)</td>
              <td className="border border-[#DDDDDD] px-2 py-1"><input type="number" value={f("f5")} onChange={ch("f5")} placeholder="예: 60" className="w-full outline-none text-sm text-[#1A1A1A] px-2" /></td>
              <td className="border border-[#DDDDDD] px-4 py-2 text-xs text-[#888]">음원·공연·MD 수익 포함</td>
            </tr>
            <tr>
              <td className="border border-[#DDDDDD] px-4 py-2 text-[#555] text-xs">아티스트 (乙)</td>
              <td className="border border-[#DDDDDD] px-2 py-1"><input type="number" value={f("f6")} onChange={ch("f6")} placeholder="예: 40" className="w-full outline-none text-sm text-[#1A1A1A] px-2" /></td>
              <td className="border border-[#DDDDDD] px-4 py-2 text-xs text-[#888]">선급금 정산 후 지급</td>
            </tr>
            <tr className="bg-[#FAFAFA]">
              <td className="border border-[#DDDDDD] px-4 py-2 text-[#555] text-xs font-semibold">합계</td>
              <td className="border border-[#DDDDDD] px-4 py-2 text-sm font-bold text-[#1A1A1A]">{(parseInt(f("f5") || "0") + parseInt(f("f6") || "0"))}%</td>
              <td className="border border-[#DDDDDD] px-4 py-2 text-xs text-[#888]">합계 100%이어야 함</td>
            </tr>
          </tbody>
        </table>

        <SectionLabel>특약 사항</SectionLabel>
        <textarea value={f("f8")} onChange={ch("f8")} placeholder="추가 계약 조건, 제한 사항, 기타 합의 사항을 구체적으로 기재하세요." className={textareaCls} rows={4} maxLength={500} />
      </div>
    );

    // ── 예산 기안서 ──────────────────────────────────────
    case "tmpl2": return (
      <div>
        <SectionLabel>기안 개요</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="프로젝트명" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: DarkNova MV 제작" className={inputCls} /></FieldRow>
          <FieldRow label="담당 부서" required>
            <select value={f("f6")} onChange={ch("f6")} className={selectCls}>
              <option value="">선택하세요</option>
              {["A&R부", "마케팅부", "제작부", "재무부", "법무부"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="집행 예정일" required><input type="date" value={f("f4")} onChange={ch("f4")} className={inputCls} /></FieldRow>
          <FieldRow label="총 예산 (만원)" required><input type="number" value={f("f2")} onChange={ch("f2")} placeholder="예: 8000" className={inputCls} /></FieldRow>
        </div>

        <SectionLabel>예산 집행 내역</SectionLabel>
        <div className="mb-4">
          <FieldRow label="집행 항목 (쉼표로 구분)" required>
            <input type="text" value={f("f3")} onChange={ch("f3")} placeholder="예: 촬영 장비 렌탈 2,000만원 / 감독비 3,000만원 / 편집비 1,500만원 / 기타 1,500만원" className={inputCls} />
          </FieldRow>
        </div>
        <div className="border border-[#E0E0E0] rounded bg-[#FAFAFA] p-3 mb-4">
          <div className="text-[10px] text-[#888] font-semibold mb-2">예산 미리보기</div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#555]">신청 예산</span>
            <span className="text-sm font-bold text-[#1A1A1A]">{parseInt(f("f2") || "0").toLocaleString()}만원</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-[#555]">가용 한도</span>
            <span className="text-sm font-bold text-[#059669]">10,000만원</span>
          </div>
          <div className="w-full bg-[#E0E0E0] rounded-full h-1.5 mt-2">
            <div className="h-1.5 rounded-full bg-[#4B6CF7] transition-all" style={{ width: `${Math.min(100, (parseInt(f("f2") || "0") / 10000) * 100)}%` }}></div>
          </div>
        </div>

        <SectionLabel>기대 효과 및 ROI</SectionLabel>
        <textarea value={f("f5")} onChange={ch("f5")} placeholder="본 예산 집행을 통해 기대되는 구체적인 효과 및 수익 예측을 기술하세요. (예: 음원 스트리밍 수익 2억원 예상, 팬덤 규모 20% 확대 등)" className={textareaCls} rows={4} maxLength={500} />
      </div>
    );

    // ── 음원 발매 기안서 ─────────────────────────────────
    case "tmpl3": return (
      <div>
        <SectionLabel>발매 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="발매 아티스트" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 루나&스타즈" className={inputCls} /></FieldRow>
          <FieldRow label="앨범/싱글 제목" required><input type="text" value={f("f2")} onChange={ch("f2")} placeholder="예: Starlight EP" className={inputCls} /></FieldRow>
          <FieldRow label="트랙 수" required><input type="number" value={f("f3")} onChange={ch("f3")} placeholder="예: 6" className={inputCls} /></FieldRow>
          <FieldRow label="발매 예정일" required><input type="date" value={f("f4")} onChange={ch("f4")} className={inputCls} /></FieldRow>
          <FieldRow label="주요 유통 플랫폼" required><input type="text" value={f("f5")} onChange={ch("f5")} placeholder="예: 멜론, 스포티파이, 애플뮤직" className={inputCls} /></FieldRow>
          <FieldRow label="마케팅 예산 (만원)" required><input type="number" value={f("f6")} onChange={ch("f6")} placeholder="예: 3000" className={inputCls} /></FieldRow>
        </div>

        <SectionLabel>앨범 콘셉트 및 기획 요약</SectionLabel>
        <textarea value={f("f7")} onChange={ch("f7")} placeholder="앨범의 주제, 타깃 오디언스(연령대·성별), 장르, 분위기, 비주얼 방향성을 구체적으로 설명하세요." className={textareaCls} rows={5} maxLength={500} />
      </div>
    );

    // ── 법적 대응 기안서 ─────────────────────────────────
    case "tmpl4": return (
      <div>
        <SectionLabel>사안 개요</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="사안 제목" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 스타라이트엔터 표절 시비 대응" className={inputCls} /></FieldRow>
          <FieldRow label="상대방 (회사/개인)" required><input type="text" value={f("f2")} onChange={ch("f2")} placeholder="예: 스타라이트 엔터테인먼트" className={inputCls} /></FieldRow>
          <FieldRow label="사안 발생일" required><input type="date" value={f("f3")} onChange={ch("f3")} className={inputCls} /></FieldRow>
          <FieldRow label="예상 법률 비용 (만원)" required><input type="number" value={f("f5")} onChange={ch("f5")} placeholder="예: 500" className={inputCls} /></FieldRow>
        </div>

        <SectionLabel>대응 방향</SectionLabel>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {["합의 협상", "법적 반박문 발송", "법적 소송 제기", "침묵 대응"].map(opt => (
            <label key={opt} className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-all ${f("f4") === opt ? "border-[#1A1A1A] bg-[#F5F5F5]" : "border-[#E0E0E0] hover:border-[#BBBBBB]"}`}>
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${f("f4") === opt ? "border-[#1A1A1A]" : "border-[#CCCCCC]"}`}>
                {f("f4") === opt && <div className="w-2 h-2 rounded-full bg-[#1A1A1A]"></div>}
              </div>
              <input type="radio" className="hidden" value={opt} checked={f("f4") === opt} onChange={ch("f4")} />
              <span className="text-sm text-[#333]">{opt}</span>
            </label>
          ))}
        </div>

        <SectionLabel>사안 상세 및 당사 입장</SectionLabel>
        <textarea value={f("f6")} onChange={ch("f6")} placeholder="분쟁의 경위, 핵심 쟁점, 당사의 법적 근거, 원하는 결과 등을 상세하게 서술하세요." className={textareaCls} rows={5} maxLength={500} />
      </div>
    );

    // ── 앨범 기획서 ──────────────────────────────────────
    case "tmpl5": return (
      <div>
        <SectionLabel>앨범 기본 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="아티스트명" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 루나&스타즈" className={inputCls} /></FieldRow>
          <FieldRow label="앨범 유형" required>
            <select value={f("f2")} onChange={ch("f2")} className={selectCls}>
              <option value="">선택하세요</option>
              {["정규", "미니앨범(EP)", "싱글", "OST"].map(o => <option key={o}>{o}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="앨범 제목 (가제)" required><input type="text" value={f("f3")} onChange={ch("f3")} placeholder="예: Starlight Vol.2" className={inputCls} /></FieldRow>
          <FieldRow label="예상 트랙 수" required><input type="number" value={f("f4")} onChange={ch("f4")} placeholder="예: 6" className={inputCls} /></FieldRow>
          <FieldRow label="발매 예정일" required><input type="date" value={f("f5")} onChange={ch("f5")} className={inputCls} /></FieldRow>
          <FieldRow label="제작 예산 (만원)" required><input type="number" value={f("f6")} onChange={ch("f6")} placeholder="예: 8000" className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>기획 방향</SectionLabel>
        <div className="mb-4">
          <FieldRow label="타겟 오디언스" required><input type="text" value={f("f7")} onChange={ch("f7")} placeholder="예: 15~28세 K-POP 팬덤, 국내외" className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>콘셉트 방향성</SectionLabel>
        <textarea value={f("f8")} onChange={ch("f8")} placeholder="앨범 세계관, 비주얼 방향, 장르, 핵심 메시지 등" className={textareaCls} rows={4} maxLength={500} />
      </div>
    );

    // ── 공연 운영 계획서 ─────────────────────────────────
    case "tmpl6": return (
      <div>
        <SectionLabel>공연 기본 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="공연명" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: K-ArtPEN 썸머 페스티벌 2026" className={inputCls} /></FieldRow>
          <FieldRow label="공연 일정" required><input type="date" value={f("f2")} onChange={ch("f2")} className={inputCls} /></FieldRow>
          <FieldRow label="공연장" required><input type="text" value={f("f3")} onChange={ch("f3")} placeholder="예: 올림픽 체조경기장" className={inputCls} /></FieldRow>
          <FieldRow label="예상 관객 수" required><input type="number" value={f("f4")} onChange={ch("f4")} placeholder="예: 7000" className={inputCls} /></FieldRow>
          <FieldRow label="총 예산 (만원)" required><input type="number" value={f("f5")} onChange={ch("f5")} placeholder="예: 71000" className={inputCls} /></FieldRow>
          <FieldRow label="주요 라인업" required><input type="text" value={f("f6")} onChange={ch("f6")} placeholder="예: 루나&스타즈, DarkNova, M.O.N.G." className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>운영 계획 요약</SectionLabel>
        <textarea value={f("f7")} onChange={ch("f7")} placeholder="스테이지 구성, 비상 대응 계획, 보안 계획, 예매 전략 등 운영 전반을 상세히 기술하세요." className={textareaCls} rows={5} maxLength={500} />
      </div>
    );

    // ── 보조금 신청 기안서 ───────────────────────────────
    case "tmpl7": return (
      <div>
        <SectionLabel>신청 개요</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="지원 기관" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 한국문화예술위원회" className={inputCls} /></FieldRow>
          <FieldRow label="사업명" required><input type="text" value={f("f2")} onChange={ch("f2")} placeholder="예: 예술창작지원사업" className={inputCls} /></FieldRow>
          <FieldRow label="신청 금액 (만원)" required><input type="number" value={f("f3")} onChange={ch("f3")} placeholder="예: 15000" className={inputCls} /></FieldRow>
          <FieldRow label="" required={false}><span /></FieldRow>
          <FieldRow label="사업 기간 (시작)" required><input type="date" value={f("f4")} onChange={ch("f4")} className={inputCls} /></FieldRow>
          <FieldRow label="사업 기간 (종료)" required><input type="date" value={f("f5")} onChange={ch("f5")} className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>사업 목표 및 수혜자</SectionLabel>
        <textarea value={f("f6")} onChange={ch("f6")} placeholder="사업 목표, 예상 수혜자 수, 사업 내용 요약 등을 구체적으로 기술하세요." className={textareaCls} rows={3} maxLength={500} />
        <SectionLabel>기대 효과</SectionLabel>
        <textarea value={f("f7")} onChange={ch("f7")} placeholder="사업 수행으로 기대되는 사회적·문화적 효과를 기술하세요." className={textareaCls} rows={3} maxLength={500} />
      </div>
    );

    // ── IP 라이선스 계약 검토 ────────────────────────────
    case "tmpl8": return (
      <div>
        <SectionLabel>계약 기본 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="작품명" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 어둠 속의 빛" className={inputCls} /></FieldRow>
          <FieldRow label="라이선스 유형" required>
            <select value={f("f2")} onChange={ch("f2")} className={selectCls}>
              <option value="">선택하세요</option>
              {["드라마 판권", "OTT 판권", "게임 IP", "굿즈 라이선싱", "해외 번역출판", "영화 판권"].map(o => <option key={o}>{o}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="협상 상대방" required><input type="text" value={f("f3")} onChange={ch("f3")} placeholder="예: Netflix Korea" className={inputCls} /></FieldRow>
          <FieldRow label="계약 금액 (만원)" required><input type="number" value={f("f4")} onChange={ch("f4")} placeholder="예: 80000" className={inputCls} /></FieldRow>
          <FieldRow label="사용 지역" required>
            <select value={f("f5")} onChange={ch("f5")} className={selectCls}>
              <option value="">선택하세요</option>
              {["국내", "아시아", "글로벌", "일본", "북미"].map(o => <option key={o}>{o}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="계약 기간 (년)" required><input type="number" value={f("f6")} onChange={ch("f6")} placeholder="예: 3" className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>계약 조건 및 검토 요청사항</SectionLabel>
        <textarea value={f("f7")} onChange={ch("f7")} placeholder="독점 여부, 2차 저작물 제작권, 해지 조건, 독소조항, 특이 요청사항 등을 상세히 기술하세요." className={textareaCls} rows={5} maxLength={500} />
      </div>
    );

    // ── 캠페인 기획안 ────────────────────────────────────
    case "tmpl9": return (
      <div>
        <SectionLabel>캠페인 기본 정보</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="캠페인명" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 하이트진로 여름 SNS 캠페인" className={inputCls} /></FieldRow>
          <FieldRow label="클라이언트" required><input type="text" value={f("f2")} onChange={ch("f2")} placeholder="예: 하이트진로" className={inputCls} /></FieldRow>
          <FieldRow label="총 예산 (만원)" required><input type="number" value={f("f3")} onChange={ch("f3")} placeholder="예: 5000" className={inputCls} /></FieldRow>
          <FieldRow label="집행 채널" required><input type="text" value={f("f4")} onChange={ch("f4")} placeholder="예: 인스타그램, 틱톡, 유튜브 쇼츠" className={inputCls} /></FieldRow>
          <FieldRow label="캠페인 목표 KPI" required><input type="text" value={f("f5")} onChange={ch("f5")} placeholder="예: 해시태그 챌린지 참여 10만건" className={inputCls} /></FieldRow>
          <FieldRow label="핵심 메시지" required><input type="text" value={f("f6")} onChange={ch("f6")} placeholder="예: #여름진로챌린지" className={inputCls} /></FieldRow>
        </div>
        <SectionLabel>타겟 오디언스</SectionLabel>
        <textarea value={f("f7")} onChange={ch("f7")} placeholder="연령대, 관심사, 행동 특성 등 타겟 오디언스를 구체적으로 기술하세요." className={textareaCls} rows={3} maxLength={500} />
        <SectionLabel>기획 방향 요약</SectionLabel>
        <textarea value={f("f8")} onChange={ch("f8")} placeholder="콘텐츠 유형, 인플루언서 전략, 바이럴 포인트, 크리에이티브 방향 등" className={textareaCls} rows={3} maxLength={500} />
      </div>
    );

    // ── 보도자료 배포 기안서 ─────────────────────────────
    case "tmpl10": return (
      <div>
        <SectionLabel>배포 개요</SectionLabel>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <FieldRow label="보도자료 제목 (헤드라인)" required><input type="text" value={f("f1")} onChange={ch("f1")} placeholder="예: 루나&스타즈, 1st EP 'Starlight' 4월 15일 공식 발매" className={inputCls} /></FieldRow>
          <FieldRow label="발신처 및 배포 예정일" required><input type="text" value={f("f2")} onChange={ch("f2")} placeholder="예: K-ArtPEN 엔터테인먼트 | 2026.04.01" className={inputCls} /></FieldRow>
        </div>
        <FieldRow label="배포 대상 언론사" required>
          <input type="text" value={f("f3")} onChange={ch("f3")} placeholder="예: 연합뉴스, 스포츠조선, 아이돌차트, 뮤직뱅크, 스타뉴스" className={inputCls} />
        </FieldRow>

        <SectionLabel>보도자료 내용</SectionLabel>
        <div className="mb-4">
          <FieldRow label="리드 문장 (Lead) — 5W1H 포함" required>
            <textarea value={f("f4")} onChange={ch("f4")} placeholder="누가·무엇을·언제·어디서·왜를 첫 2~3문장에 압축하세요. 기자들은 첫 단락만 읽어도 기사화 여부를 결정합니다." className={textareaCls} rows={4} maxLength={500} />
          </FieldRow>
        </div>
        <div className="mb-4">
          <FieldRow label="보도자료 본문 요약" required>
            <textarea value={f("f5")} onChange={ch("f5")} placeholder="발매 배경, 앨범 구성, 아티스트 코멘트 등 핵심 내용을 2~3문단으로 작성하세요." className={textareaCls} rows={5} maxLength={500} />
          </FieldRow>
        </div>

        <SectionLabel>인용구 및 연락처</SectionLabel>
        <div className="mb-4">
          <FieldRow label="공식 인용구 (Quote)">
            <textarea value={f("f6")} onChange={ch("f6")} placeholder='예: K-ArtPEN 대표는 "루나&스타즈의 데뷔는 K-POP 씬에 새로운 바람을 불러올 것"이라고 밝혔다.' className={textareaCls} rows={3} maxLength={300} />
          </FieldRow>
        </div>
        <FieldRow label="담당자 연락처" required>
          <input type="text" value={f("f7")} onChange={ch("f7")} placeholder="예: 마케팅팀 김소현 | press@karten.co.kr | 02-1234-5678" className={inputCls} />
        </FieldRow>
      </div>
    );

    default: return <div className="text-[#888] text-sm">지원하지 않는 양식입니다.</div>;
  }
}

// ─── 필수 필드 완료 여부 체크 ────────────────────────────
function checkComplete(templateId: string, data: Record<string, string>, template: { fields: { id: string; required?: boolean }[] }): boolean {
  return template.fields.filter(f => f.required).every(f => data[f.id]?.trim());
}

// ─── 메인 DocumentEditor ────────────────────────────────
export default function DocumentEditor({ templateId, onSubmit, onCancel, resubmitInfo }: Props) {
  const { jobRole, sendNotification } = useRole();
  const { profile } = useAuth();
  const template = approvalTemplates.find(t => t.id === templateId);
  const [data, setData] = useState<Record<string, string>>({});
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<{ passed: boolean; message: string } | null>(null);
  const [notifSent, setNotifSent] = useState(false);

  const docNo = useState(() => genDocNo(templateId))[0];
  const today = new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
  const submitter = profile?.display_name ?? ROLE_LABEL[jobRole] ?? "기안자";
  const dept = ROLE_LABEL[jobRole] ?? jobRole;

  const onChange = useCallback((id: string, val: string) => {
    setData(prev => ({ ...prev, [id]: val }));
    setResult(null);
  }, []);

  if (!template) return null;

  const isComplete = checkComplete(templateId, data, template);

  const handleValidate = () => {
    if (!isComplete) return;
    setValidating(true);
    setTimeout(() => {
      setValidating(false);
      setResult(validate(templateId, data));
    }, 1800);
  };

  const handleSubmit = async () => {
    if (!result?.passed) return;
    const rule = workflowRules[jobRole]?.[templateId];
    if (rule && !notifSent) {
      try { await sendNotification(rule.toRole, rule.type, rule.buildTitle(data), rule.buildBody(data)); setNotifSent(true); }
      catch { /* ignore */ }
    }
    onSubmit(data);
  };

  return (
    <div className="flex flex-col h-full bg-[#E8E8E8]">
      {/* 상단 툴바 */}
      <div className="bg-[#F5F5F5] border-b border-[#DDDDDD] px-5 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onCancel} className="text-[#888] hover:text-[#444] cursor-pointer transition-colors flex items-center gap-1.5 text-xs">
            <i className="ri-arrow-left-line"></i> 목록으로
          </button>
          <div className="w-px h-4 bg-[#DDDDDD]"></div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${template.icon ?? "ri-file-line"} text-[#555] text-sm`}></i>
            </div>
            <span className="text-[#333] text-xs font-semibold">{template.title}</span>
            <span className="text-[#AAAAAA] text-[10px]">{docNo}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {notifSent && (
            <span className="text-[10px] text-[#059669] bg-[#059669]/10 border border-[#059669]/20 px-2 py-1 rounded flex items-center gap-1">
              <i className="ri-notification-3-line text-xs"></i> 담당팀 알림 발송됨
            </span>
          )}
          {result && (
            <span className={`text-[10px] px-2 py-1 rounded border flex items-center gap-1 font-semibold ${result.passed ? "text-[#16A34A] bg-[#F0FDF4] border-[#BBF7D0]" : "text-[#DC2626] bg-[#FFF5F5] border-[#FECACA]"}`}>
              <i className={result.passed ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"}></i>
              {result.passed ? "AI 검토 통과" : "AI 검토 반려"}
            </span>
          )}
          <button
            type="button"
            onClick={handleValidate}
            disabled={!isComplete || validating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#CCCCCC] bg-white hover:bg-[#F5F5F5] text-[#555] text-xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap"
          >
            {validating ? <><i className="ri-loader-4-line animate-spin text-xs"></i> AI 검토 중...</> : <><i className="ri-robot-line text-xs"></i> AI 사전검토</>}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!result?.passed}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded text-white text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all whitespace-nowrap ${result?.passed ? "bg-[#16A34A] hover:bg-[#15803D] animate-[pulse_1.5s_ease-in-out_2]" : "bg-[#1A1A1A] hover:bg-[#333]"}`}
          >
            <i className="ri-send-plane-line text-xs"></i> 결재 올리기
          </button>
        </div>
      </div>

      {/* 재기안 모드 배너 */}
      {resubmitInfo && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-start gap-3 shrink-0">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 border border-amber-300 shrink-0 mt-0.5">
            <i className="ri-refresh-line text-amber-600 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-amber-800 text-xs font-bold mb-0.5">재기안 모드 — 반려 문서 수정 중</p>
            <p className="text-amber-700 text-[11px] leading-relaxed">원본: <span className="font-semibold">{resubmitInfo.title}</span></p>
            <div className="mt-1.5 bg-amber-100 border border-amber-200 rounded-lg px-3 py-2">
              <p className="text-[10px] text-amber-700 font-semibold mb-1">반려 사유 (수정 필요)</p>
              <p className="text-[11px] text-amber-800 leading-relaxed">{resubmitInfo.aiComment.replace("❌ 반려. ", "")}</p>
            </div>
          </div>
        </div>
      )}

      {/* 문서 본체 */}
      <div className="flex-1 overflow-y-auto py-8 px-8">
        <div className="max-w-3xl mx-auto bg-white shadow-sm relative overflow-hidden" style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)", borderTop: result ? (result.passed ? "3px solid #16A34A" : "3px solid #DC2626") : "3px solid transparent", transition: "border-color 0.4s ease" }}>
          
          {/* 검토 상태 워터마크 */}
          {result && (
            <div className="absolute pointer-events-none inset-0 flex items-center justify-center opacity-[0.03] z-0" aria-hidden>
              <span className="text-[160px] font-black rotate-[-25deg] select-none" style={{ color: result.passed ? "#16A34A" : "#DC2626" }}>
                {result.passed ? "承認" : "否決"}
              </span>
            </div>
          )}

          {/* 검토 완료 오버레이 도장 (우측 상단) */}
          {result && (
            <div className="absolute top-10 right-10 pointer-events-none z-10 animate-[fadeIn_0.4s_ease]">
              <div
                className="w-20 h-20 rounded-full border-4 flex flex-col items-center justify-center"
                style={{ borderColor: result.passed ? "#16A34A" : "#DC2626", opacity: result.passed ? 0.7 : 0.65, transform: "rotate(-12deg)", boxShadow: `0 0 0 2px ${result.passed ? "#16A34A22" : "#DC262622"}` }}
              >
                <span className="text-xl font-bold leading-none" style={{ color: result.passed ? "#16A34A" : "#DC2626", fontFamily: "serif" }}>{result.passed ? "承認" : "否決"}</span>
                <span className="text-[8px] font-medium" style={{ color: result.passed ? "#16A34A" : "#DC2626" }}>AI 검토</span>
              </div>
            </div>
          )}

          <div className="px-14 py-12 relative z-0" style={{ fontFamily: "'Noto Serif KR', 'Pretendard Variable', serif" }}>
            {/* 문서 헤더 */}
            <DocHeader docNo={docNo} date={today} submitter={submitter} dept={dept} />

            {/* 구분선 */}
            <div className="border-t-2 border-[#1A1A1A] mt-4 mb-2"></div>
            <div className="border-t border-[#CCCCCC] mb-8"></div>

            {/* 문서 제목 */}
            <div className="text-center mb-2">
              <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-wider" style={{ letterSpacing: "0.15em" }}>
                {template.title.replace(/\s/g, "\u3000")}
              </h1>
            </div>
            <div className="border-b-2 border-[#1A1A1A] mb-1 mx-auto" style={{ width: "60%" }}></div>
            <div className="border-b border-[#999] mb-10 mx-auto" style={{ width: "60%" }}></div>

            {/* 템플릿별 본문 */}
            <TemplateBody templateId={templateId} data={data} onChange={onChange} />

            {/* AI 검토 결과 */}
            {result && (
              <div className={`mt-8 rounded-xl border-2 overflow-hidden animate-[fadeIn_0.5s_ease] ${result.passed ? "border-[#16A34A]/30" : "border-[#DC2626]/30"}`}>
                {/* 결과 헤더 */}
                <div className={`px-5 py-3 flex items-center gap-3 ${result.passed ? "bg-[#16A34A] text-white" : "bg-[#DC2626] text-white"}`}>
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20 shrink-0">
                    <i className={`${result.passed ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} text-white text-base`}></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">AI 법무 시스템 검토 결과 — {result.passed ? "승인 가능" : "반려"}</p>
                    <p className="text-[10px] text-white/70 mt-0.5">
                      {result.passed ? "이 문서는 결재 올리기 기준을 충족합니다." : "아래 사유를 해결한 후 재검토 요청하세요."}
                    </p>
                  </div>
                  {!result.passed && (
                    <button
                      type="button"
                      onClick={handleValidate}
                      disabled={validating}
                      className="shrink-0 flex items-center gap-1.5 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-medium"
                    >
                      <i className="ri-refresh-line"></i> 재검토
                    </button>
                  )}
                </div>
                {/* 결과 본문 */}
                <div className={`px-5 py-4 ${result.passed ? "bg-[#F0FDF4]" : "bg-[#FFF5F5]"}`}>
                  <p className={`text-sm leading-relaxed ${result.passed ? "text-[#166534]" : "text-[#991B1B]"}`}>{result.message}</p>
                  {result.passed && (
                    <div className="mt-3 flex items-center gap-2 text-[#16A34A] text-xs font-medium">
                      <i className="ri-arrow-right-circle-line"></i>
                      상단 <strong>결재 올리기</strong> 버튼이 활성화되었습니다. 클릭해 결재를 제출하세요.
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isComplete && (
              <div className="mt-6 flex items-center gap-2 text-[#BBBBBB] text-[11px]">
                <i className="ri-information-line text-sm"></i>
                <span>필수 항목(*)을 모두 입력하면 AI 사전검토를 진행할 수 있습니다.</span>
              </div>
            )}

            {/* 하단 서명란 */}
            <div className="mt-12 pt-6 border-t border-[#E0E0E0]">
              <div className="flex justify-between items-end">
                <p className="text-[11px] text-[#888]">
                  위와 같이 결재를 요청합니다.<br />
                  <span className="text-[10px] text-[#BBBBBB]">{today} 기안 · K-ArtPEN 엔터테인먼트</span>
                </p>
                <div className="text-center">
                  <div className="border border-[#DDDDDD] w-24 h-16 flex items-center justify-center rounded">
                    <span className="text-[10px] text-[#CCCCCC]">기안자 서명</span>
                  </div>
                  <p className="text-[10px] text-[#888] mt-1">{submitter}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
